import { memo, useEffect, useState } from "react";

import ChevronLeft from "@zenith/assets/icons/ChevronLeft";

import { useMobileVerificationContext } from "@zenith/context";
import { AppNavigation } from "@zenith/hooks";
import {
  TextFieldComponent,
  SelectComponent,
  MajorButtonComponent,
  ButtonComponent,
  ToastSnackbarComponent,
  FacialStepUpComponent,
} from "@zenith/components";
import { SelectItemsType } from "@zenith/components/ui/SelectComponent/select-component/interfaces";
import {
  ApiResponseDTOListCountrySummarizedDTO,
  CountrySummarizedDTO,
  PhoneNumberAccountLookupResponseDTO,
  ResourceApiService,
  SubmitUtilityBvnSelfieVerifyRequestDTO,
  UtilityApiService,
} from "@zenith/services/user";
import { countryCodeToEnum, getCountryFlag } from "@zenith/utils";
import { getPhoneNumberLength } from "@zenith/features/globalUtils";
import { CREATE_NEW_PIN, MOBILE_VERIFICATION, SIGN_IN } from "@zenith/routes/paths";
import { setPhoneNumberValidations } from "@zenith/features/Authentication/validations/AuthValidations";
import { FormFieldErrorType } from "@zenith/types";
import {
  NUMBER_INPUT_REGEX,
  DEFAULT_CHANNEL,
  DEFAULT_PURPOSE,
} from "@zenith/data/constants";
import { useNotificationContext } from "@zenith/context";
import { SendOtpDTO } from "@zenith/services/user";
import WalletIcon from "@zenith/assets/icons/WalletIcon";
import { Logo } from "@zenith/shared";
import { usePublicFacialVerification } from "@zenith/hooks/usePublicFacialVerification";

// BOAS-8976: same facial-eligibility set as useRegistrationType's
// FACIAL_ELIGIBLE_TYPES, against the account-lookup response's own
// (structurally identical but separately generated) registrationType enum.
// A phone with no registrationType on record - onboarding started but not
// finished - also routes to OTP directly instead of attempting facial.
const FACIAL_ELIGIBLE_LOOKUP_TYPES: ReadonlySet<PhoneNumberAccountLookupResponseDTO.registrationType> =
  new Set([
    PhoneNumberAccountLookupResponseDTO.registrationType.INDIVIDUAL,
    PhoneNumberAccountLookupResponseDTO.registrationType.SOLE_PROPRIETORSHIP,
  ]);

/* eslint-disable-next-line */
export interface ForgotPinPageProps {
  isNewUser?: boolean;
}

const ForgotPinPage = memo(function ForgotPinPage(props: ForgotPinPageProps) {
  const { isNewUser } = props;
  const { navigateTo } = AppNavigation();
  const { setCountryCode, setPhoneNumber, setIsNewUser } =
    useMobileVerificationContext();
  const { setOpen } = useNotificationContext();

  const [countries, setCountries] = useState<SelectItemsType[]>([]);
  const [phoneNumberError, setPhoneNumberError] = useState<FormFieldErrorType>({
    message: "",
    hasError: false,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formData, setFormData] = useState<{
    countryCode: string;
    phoneNumber: string;
  }>({
    countryCode: "",
    phoneNumber: "",
  });

  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  // BOAS-7938/8976/8726: individual/sole-prop merchants step up with a facial
  // check instead of OTP; multi-signatory phones, and phones with no
  // registrationType on record, use the existing OTP flow. There is no session to
  // route on upfront here (unauthenticated), so eligibility comes from an
  // account-lookup call in handleSubmit rather than from a session role.
  const [pageStep, setPageStep] = useState<"phone" | "facialStepUp">("phone");
  const passcodeResetStepUp = usePublicFacialVerification(
    SubmitUtilityBvnSelfieVerifyRequestDTO.purpose.PASSCODE_RESET
  );

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  useEffect(() => {
    setIsNewUser(isNewUser as boolean);

    ResourceApiService.getCountries()
      .then((res: ApiResponseDTOListCountrySummarizedDTO) => {
        if (!res.data) return;
        const items = res.data.map((country: CountrySummarizedDTO) => ({
          value: country.countryCode as string,
          label: country.countryCallingCode as string,
          icon: getCountryFlag(country.countryCode as string),
          countryName: country.countryName,
        }));

        setCountries(items);

        if (items.length > 0) {
          setFormData((prevFormData) => ({
            ...prevFormData,
            phoneNumber: "",
            countryCode: items[0].value,
          }));
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }, []);

  const onCountryCodeChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null
  ): void => {
    if (event && newValue)
      setFormData({ ...formData, countryCode: newValue as string });
  };

  const onPhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setFormData({
      ...formData,
      phoneNumber: event.target.value.replace(NUMBER_INPUT_REGEX, "") as string,
    });
  };

  const onPhoneNumberBlur = (): void => {
    setPhoneNumberError(
      setPhoneNumberValidations(
        formData.phoneNumber,
        getPhoneNumberLength(formData.countryCode, formData.phoneNumber)
      )
    );
  };

  //#region Aux Methods

  const handleBackButton = (): void => {
    navigateTo(SIGN_IN);
  };

  const mapCountryCodeToPhoneNumber = (): string => {
    switch (formData.countryCode) {
      case "NG":
        return "+234" + formData.phoneNumber;
      case "GH":
        return "+233" + formData.phoneNumber;
      default:
        throw new Error("country code not mapped");
    }
  };

  //#endregion Aux Methods

  const proceedWithOtp = async (
    fullPhoneNumber: string,
    noRegistrationType = false
  ): Promise<void> => {
    // A reset needs no pre-check here: the account lookup already confirmed
    // the number, and OTPComponent sends the code when the OTP page opens.
    // Sending here as well meant the user got two codes.
    if (!isNewUser) {
      setIsLoading(false);
      // BOAS-8726: with no registrationType on record the reset goes through
      // the team passcode endpoint, authorised by the OTP's X-MFA token
      navigateTo(
        MOBILE_VERIFICATION,
        noRegistrationType ? { state: { noRegistrationType } } : undefined
      );
      return;
    }

    try {
      const response = await UtilityApiService.sendOtp({
        channel: DEFAULT_CHANNEL as SendOtpDTO.channel,
        countryCode: countryCodeToEnum(
          formData.countryCode
        ) as SendOtpDTO.countryCode,
        phoneNumber: fullPhoneNumber,
        purpose: DEFAULT_PURPOSE,
        platform: SendOtpDTO.platform.SMS,
      });

      const setupComplete = (response.data as any)?.setupComplete;

      if (setupComplete === true) {
        notify(
          "User Already Exists",
          "This onboarding link has already been used",
          "error"
        );
        setTimeout(() => {
          navigateTo(SIGN_IN);
        }, 2000);
        return;
      }

      navigateTo(MOBILE_VERIFICATION);
    } catch (error) {
      notify(
        "Error",
        "Unable to process your request. Please try again.",
        "error"
      );
      console.error("Error checking user status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formData.phoneNumber.length) {
      setPhoneNumberError({
        message: "Please enter your phone number",
        hasError: true,
      });
      return;
    }

    const fullPhoneNumber = mapCountryCodeToPhoneNumber();
    // Both pages downstream (CreateNewPinPage, MobileVerificationPage) read
    // phone/country off this shared context regardless of which path
    // (facial or OTP) ends up being used.
    setPhoneNumber(fullPhoneNumber || "");
    setCountryCode(countryCodeToEnum(formData.countryCode) || "");

    // "Set Up Passcode" (new user, first-time) has no existing credential
    // to verify facially against - OTP only, unchanged.
    if (isNewUser) {
      setIsLoading(true);
      await proceedWithOtp(fullPhoneNumber);
      return;
    }

    // BOAS-8976: look up the account upfront instead of reactively trying
    // facial and catching its 409 - lets an onboarding-incomplete phone
    // (no registrationType on record yet) go straight to OTP instead of
    // bouncing through a facial attempt that was never going to work.
    setIsLoading(true);
    try {
      const { data } = await UtilityApiService.lookupAccountByPhoneNumber({
        phoneNumber: fullPhoneNumber,
      });

      if (!data?.exists) {
        notify(
          "Account Not Found",
          "We couldn't find an account for this phone number.",
          "error"
        );
        setIsLoading(false);
        return;
      }

      // BOAS-8726: no registrationType on record means onboarding stopped
      // before it was chosen - OTP only. Otherwise the usual routing applies.
      const isFacialEligible =
        !!data.registrationType &&
        FACIAL_ELIGIBLE_LOOKUP_TYPES.has(data.registrationType);

      if (isFacialEligible) {
        setIsLoading(false);
        setPageStep("facialStepUp");
      } else {
        await proceedWithOtp(fullPhoneNumber, !data.registrationType);
      }
    } catch (error) {
      notify(
        "Error",
        "Unable to process your request. Please try again.",
        "error"
      );
      console.error("Error looking up account:", error);
      setIsLoading(false);
    }
  };

  const onFacialStepUpVerified = (jobId: string) => {
    navigateTo(CREATE_NEW_PIN, { state: { jobId } });
  };

  const onFacialStepUpNotEligible = () => {
    // 409: the account-lookup guess in handleSubmit said facial-eligible but
    // the server disagrees - fall back to OTP rather than being unsafe.
    setIsLoading(true);
    proceedWithOtp(mapCountryCodeToPhoneNumber());
  };

  const backFromFacialStepUp = () => {
    setPageStep("phone");
  };

  const disableContinue = (): boolean => {
    if (!formData.countryCode) return true;
    if (
      (formData.countryCode === "NG" &&
        formData.phoneNumber.length <
          getPhoneNumberLength("NG", formData.phoneNumber)) ||
      (formData.countryCode === "GH" &&
        formData.phoneNumber.length <
          getPhoneNumberLength("GH", formData.phoneNumber))
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className="my-3 flex h-screen  w-full grow overflow-x-hidden sm:w-5/6 md:mx-auto md:w-1/2 lg:w-3/4 xl:w-[55%]">
      <ToastSnackbarComponent
        horizontal="center"
        status={toasterData.status}
        vertical="top"
        hasAutoHideDuration
        size="lg"
        title={toasterData.title}
        text={toasterData.message}
      />
      <div className="flex w-full grow flex-col justify-center px-3 md:px-0">
        <div className="absolute top-8 left-1/2 flex -translate-x-1/2 gap-2 lg:hidden">
          <WalletIcon height={40} width={40} className="my-auto" />
          <Logo />
        </div>
        <div className="flex items-start">
          <ButtonComponent
            label="Back"
            onClick={handleBackButton}
            id="back-to-sign-in"
            size="large"
            type="tertiary"
            iconStart={<ChevronLeft />}
          />
        </div>

        <div className="mb-16 mt-16 flex flex-col items-center justify-center">
          <h5 className="mb-4 text-2xl md:text-4xl">
            {isNewUser ? "Set Up Passcode" : "Forgot your Passcode?"}
          </h5>
          <span className="text-neutral-600">
            {isNewUser
              ? "Insert your number and create a Passcode"
              : "Provide your phone number to create a new Passcode"}
          </span>
        </div>
        {pageStep === "facialStepUp" ? (
          // BOAS-7938: facial step-up, slots in after phone entry.
          <div className="mx-auto w-full">
            <FacialStepUpComponent
              state={{
                ...passcodeResetStepUp,
                submit: (photos) =>
                  passcodeResetStepUp.submit(
                    mapCountryCodeToPhoneNumber(),
                    photos
                  ),
              }}
              onVerified={onFacialStepUpVerified}
              onBack={backFromFacialStepUp}
              onNotEligible={onFacialStepUpNotEligible}
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mx-auto w-full" noValidate>
            <div className="flex flex-col">
              <div className="flex flex-row">
                <SelectComponent
                  id="forgot-pin-country-code-dropdown"
                  label="Country"
                  labelId="country-code-dropdown-label"
                  className=""
                  defaultValue=""
                  value={formData.countryCode}
                  onChange={onCountryCodeChange}
                  items={countries}
                  editable={false}
                />
                <TextFieldComponent
                  id="forgot-pin-phone-number-field"
                  labelId="forgot-pin-phone-number-field-label"
                  className="w-5/6 grow xsm:w-full"
                  containerClassName="ml-5 flex grow"
                  hasError={phoneNumberError.hasError}
                  maxLength={getPhoneNumberLength(
                    formData.countryCode,
                    formData.phoneNumber
                  )}
                  minLength={getPhoneNumberLength(
                    formData.countryCode,
                    formData.phoneNumber
                  )}
                  type="tel"
                  label="Phone number"
                  placeholder="Enter phone number"
                  value={formData.phoneNumber}
                  onChange={onPhoneNumberChange}
                  onBlur={onPhoneNumberBlur}
                />
              </div>

              {phoneNumberError.hasError && (
                <div className="mt-1 w-80 text-red-400">
                  <span className="break-words">{phoneNumberError.message}</span>
                </div>
              )}
            </div>

            <div className="mt-20 flex flex-col items-center space-y-5">
              <MajorButtonComponent
                id="continue-to-mobile-verification"
                buttonType="primary"
                size="large"
                type="submit"
                disabled={disableContinue() || isLoading}
                isLoading={isLoading}
              >
                Continue
              </MajorButtonComponent>
            </div>
          </form>
        )}
      </div>
    </div>
  );
});

export default ForgotPinPage;
